import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { CHUNK_RELOAD_KEY } from "@shared/constants/index";

interface Props {
  children?: ReactNode;
  fallback?: React.ComponentType<{
    error: Error;
    resetErrorBoundary: () => void;
  }>;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Detect chunk load errors (stale chunks after deployment)
    // Align with main.tsx unhandledrejection handler patterns
    const isChunkLoadError =
      error.message?.includes("Failed to fetch dynamically imported module") ||
      error.message?.includes("error loading dynamically imported module") ||
      error.message?.includes("ChunkLoadError");

    if (isChunkLoadError) {
      // Attempt one-time reload (main.tsx handles most cases, but this catches boundary-level errors)
      try {
        const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);
        if (!hasReloaded) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");
          setTimeout(() => {
            window.location.reload();
          }, 1000);
          return;
        }
      } catch {
        // sessionStorage unavailable (private/incognito mode) — skip reload, show error
      }
    }

    // You can also log the error to an error reporting service
    // eslint-disable-next-line no-console
    console.error("Uncaught error:", error, errorInfo);
  }

  public resetErrorBoundary = (): void => {
    this.setState({ hasError: false, error: null });
  };

  public render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return (
          <FallbackComponent
            error={this.state.error}
            resetErrorBoundary={this.resetErrorBoundary}
          />
        );
      }
      return (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <h1>Something went wrong.</h1>
          <button onClick={this.resetErrorBoundary}>Try again</button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
